import {
  type EstateSnapshot,
  HANDOVER_KIND_LABELS,
  HANDOVER_STATUS_LABELS,
  type HandoverKind,
  type HandoverStatus,
  type HandoverSummary,
} from '@acasa/shared'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  pdfUrl,
  useCreateHandover,
  useDeleteHandover,
  useEstateLookup,
  useHandovers,
  useMoveInFor,
} from '@/features/handovers/use-handovers'

const KINDS: HandoverKind[] = ['rental_movein', 'rental_moveout', 'sale_handover']

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Step 1 of a new protocol: find the object.
 *
 * The lookup searches the local mirror and falls back to onOffice, so an
 * archived object — the normal case for a move-out years after the tenancy
 * started — still resolves. An unknown number is allowed through as well; the
 * protocol is more useful half-filled than not written at all.
 */
function NewProtocolDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const [kind, setKind] = useState<HandoverKind>('rental_movein')
  const [term, setTerm] = useState('')
  const [picked, setPicked] = useState<EstateSnapshot | null>(null)

  const lookup = useEstateLookup(term)
  const create = useCreateHandover()
  const moveIn = useMoveInFor(kind === 'rental_moveout' ? (picked?.estateId ?? null) : null)

  const start = async (referenceProtocolId: string | null) => {
    const detail = await create.mutateAsync({
      kind,
      estateId: picked?.estateId ?? null,
      externalNumber: picked?.externalNumber ?? (term.trim() || null),
      referenceProtocolId,
    })
    navigate(`/protokolle/${detail.id}`)
  }

  return (
    <div className="bg-ink/40 fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6">
      <div className="bg-surface border-line max-h-full w-full max-w-lg overflow-auto border p-5">
        <h2 className="font-display text-ink text-lg">Neues Übergabeprotokoll</h2>

        <fieldset className="mt-4">
          <legend className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">Art</legend>
          <div className="grid gap-2">
            {KINDS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setKind(option)}
                className={`border px-3 py-2.5 text-left text-sm transition-colors ${
                  kind === option
                    ? 'border-brand bg-brand-soft text-brand font-medium'
                    : 'border-line text-muted hover:text-ink'
                }`}
              >
                {HANDOVER_KIND_LABELS[option]}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="mt-5">
          {/* Wrapping rather than htmlFor: the association then cannot break if
              the id is ever changed or duplicated by a second dialog. */}
          <label className="block">
            <span className="text-faint mb-2 block text-[10px] tracking-[0.14em] uppercase">
              Objekt-Nr. oder Bezeichnung
            </span>
            <input
              value={term}
              // The dialog opens for one purpose: typing an object number. A
              // click into the field first would be a step with no decision in it.
              // biome-ignore lint/a11y/noAutofocus: see above
              autoFocus
              onChange={(event) => {
                setTerm(event.target.value)
                setPicked(null)
              }}
              placeholder="z. B. 2024-103"
              className="border-line focus:border-brand bg-canvas text-ink placeholder:text-faint w-full border px-3 py-2.5 text-sm outline-none"
            />
          </label>

          {lookup.isFetching && <p className="text-faint mt-2 text-[11px]">sucht …</p>}

          {lookup.data && lookup.data.matches.length > 0 && (
            <ul className="border-line mt-2 max-h-56 overflow-auto border">
              {lookup.data.matches.map((match) => (
                <li key={`${match.estateId}-${match.externalNumber}`}>
                  <button
                    type="button"
                    onClick={() => setPicked(match)}
                    className={`hover:bg-canvas block w-full px-3 py-2 text-left text-xs transition-colors ${
                      picked?.estateId === match.estateId ? 'bg-brand-soft' : ''
                    }`}
                  >
                    <span className="text-ink font-mono">{match.externalNumber ?? '—'}</span>
                    <span className="text-muted"> · {match.title ?? 'ohne Bezeichnung'}</span>
                    <span className="text-faint block">
                      {[match.street, match.houseNumber].filter(Boolean).join(' ')}
                      {match.city ? `, ${match.city}` : ''}
                      {match.fetchedLive && ' · direkt aus onOffice'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {lookup.data && term.trim().length >= 2 && lookup.data.matches.length === 0 && (
            <p className="text-muted mt-2 text-[11px]">
              Kein Objekt gefunden. Das Protokoll lässt sich trotzdem anlegen — die Objektdaten
              werden dann von Hand eingetragen.
            </p>
          )}
        </div>

        {kind === 'rental_moveout' && moveIn.data?.moveIn && (
          <div className="border-brand/30 bg-brand-soft mt-4 border p-3">
            <p className="text-ink text-xs font-medium">Einzugsprotokoll vorhanden</p>
            <p className="text-muted mt-1 text-[11px]">
              Vom {formatDate(moveIn.data.moveIn.handoverAt ?? moveIn.data.moveIn.completedAt)}.
              Räume, Schlüsselarten und Zählernummern können übernommen werden — die Zählerstände
              natürlich nicht.
            </p>
          </div>
        )}

        {create.isError && (
          <p className="text-danger mt-3 text-xs">
            {create.error instanceof Error ? create.error.message : 'Anlegen fehlgeschlagen'}
          </p>
        )}

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-line text-muted hover:text-ink border px-4 py-2.5 text-xs transition-colors"
          >
            Abbrechen
          </button>

          {kind === 'rental_moveout' && moveIn.data?.moveIn && (
            <button
              type="button"
              disabled={create.isPending}
              onClick={() => void start(moveIn.data.moveIn?.id ?? null)}
              className="border-brand text-brand hover:bg-brand-soft border px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
            >
              Aus Einzugsprotokoll übernehmen
            </button>
          )}

          <button
            type="button"
            disabled={create.isPending || term.trim().length === 0}
            onClick={() => void start(null)}
            className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors disabled:opacity-40"
          >
            {create.isPending ? 'wird angelegt …' : 'Protokoll anlegen'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** A change under way is warning-toned: the valid document is the one before it. */
const STATUS_TONES: Record<HandoverStatus, string> = {
  draft: 'border-line text-faint',
  completed: 'border-done/40 bg-done/10 text-done',
  amending: 'border-warning/40 bg-warning/10 text-warning',
}

const SECTIONS: { status: HandoverStatus; title: string }[] = [
  { status: 'draft', title: 'Entwürfe' },
  { status: 'amending', title: 'In Änderung' },
  { status: 'completed', title: 'Abgeschlossen' },
]

function ProtocolRow({ protocol }: { protocol: HandoverSummary }) {
  const remove = useDeleteHandover()
  const address = [protocol.estate.street, protocol.estate.houseNumber].filter(Boolean).join(' ')

  return (
    <li className="border-line bg-surface border">
      <div className="flex flex-wrap items-start gap-3 p-3 sm:p-4">
        <Link to={`/protokolle/${protocol.id}`} className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-ink tnum font-mono text-sm font-medium">
              {protocol.estate.externalNumber ?? '—'}
            </span>
            <span className="text-muted text-xs">{HANDOVER_KIND_LABELS[protocol.kind]}</span>
            <span className={`border px-1.5 py-0.5 text-[10px] ${STATUS_TONES[protocol.status]}`}>
              {HANDOVER_STATUS_LABELS[protocol.status]}
            </span>
          </div>

          <p className="text-muted mt-1 truncate text-xs">
            {address || protocol.estate.title || 'ohne Adresse'}
            {protocol.estate.city && `, ${protocol.estate.city}`}
          </p>

          <p className="text-faint mt-1 flex flex-wrap gap-x-3 text-[11px]">
            <span>Übergabe {formatDate(protocol.handoverAt)}</span>
            {protocol.version >= 2 && <span>Fassung {protocol.version}</span>}
            {protocol.photoCount > 0 && <span>{protocol.photoCount} Fotos</span>}
            {protocol.defectCount > 0 && (
              <span className="text-danger">{protocol.defectCount} Mängel</span>
            )}
          </p>
        </Link>

        <div className="flex shrink-0 items-center gap-2">
          {protocol.hasPdf && (
            <a
              href={pdfUrl(protocol.id)}
              target="_blank"
              rel="noreferrer"
              className="border-line text-muted hover:text-ink border px-2.5 py-1.5 text-xs transition-colors"
            >
              PDF
            </a>
          )}
          {protocol.status === 'draft' && (
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Diesen Entwurf mit allen Fotos löschen?')) {
                  remove.mutate(protocol.id)
                }
              }}
              className="text-faint hover:text-danger px-1 text-xs underline underline-offset-2"
            >
              löschen
            </button>
          )}
        </div>
      </div>
    </li>
  )
}

export function HandoversPage() {
  const { data, isPending, isError, error } = useHandovers()
  const [creating, setCreating] = useState(false)

  return (
    <div className="h-full overflow-auto px-4 py-6">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="bg-brand mb-3 block h-[3px] w-8" aria-hidden />
            <h1 className="font-display text-ink text-[26px] leading-tight">Übergabeprotokolle</h1>
            <p className="text-muted mt-2 text-sm">
              Einzug, Auszug und Objektübergabe — mit Zählerständen, Fotos und Unterschrift.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setCreating(true)}
            className="bg-brand text-surface hover:bg-brand-strong px-4 py-2.5 text-xs font-medium transition-colors"
          >
            Neues Protokoll
          </button>
        </header>

        {isPending && <p className="text-muted py-12 text-center text-sm">Wird geladen …</p>}

        {isError && (
          <p className="border-danger/30 bg-danger/5 text-danger border px-3 py-2 text-xs">
            {error instanceof Error ? error.message : 'Unbekannter Fehler'}
          </p>
        )}

        {data && data.protocols.length === 0 && (
          <div className="border-line text-muted border border-dashed px-4 py-12 text-center text-sm">
            Noch kein Protokoll angelegt.
          </div>
        )}

        {SECTIONS.map(({ status, title }) => {
          const rows = data?.protocols.filter((protocol) => protocol.status === status) ?? []
          if (rows.length === 0) return null
          return (
            <section key={status} className="mb-8 last:mb-0">
              <h2 className="text-faint mb-2 text-[10px] tracking-[0.14em] uppercase">
                {title} ({rows.length})
              </h2>
              <ul className="space-y-2">
                {rows.map((protocol) => (
                  <ProtocolRow key={protocol.id} protocol={protocol} />
                ))}
              </ul>
            </section>
          )
        })}
      </div>

      {creating && <NewProtocolDialog onClose={() => setCreating(false)} />}
    </div>
  )
}
